/**
 * TweakControl — lightweight performance control for a single endpoint.
 *
 * No star button, no endpoint chrome. Used by TweakablePanel.
 *
 * Supports two modes:
 * - Instance mode: pass `instanceId` — reads/writes via instance values
 * - Definition mode: pass `definitionId` — reads/writes via definition config
 */

import { useCallback } from 'react';
import { useRuntime } from '../../contexts/RuntimeProvider';
import { TweakSlider } from '../../shared/valueControls/TweakSlider';
import { BooleanControl } from '../../shared/valueControls/BooleanControl';
import { SelectControl } from '../../shared/valueControls/SelectControl';
import { StringControl } from '../../shared/valueControls/StringControl';
import { ColorControl } from '../../shared/valueControls/ColorControl';
import { ArraySliderControl } from '../../shared/valueControls/ArraySliderControl';
import { getEndpointColor } from '../../shared/utils';
import type { EndpointState } from '@/nodes/Endpoint';

type TweakControlProps = {
  nodeId: string;
  endpoint: EndpointState;
} & (
  | { instanceId: string; definitionId?: undefined }
  | { definitionId: string; instanceId?: undefined }
);

export function TweakControl({ instanceId, definitionId, nodeId, endpoint }: TweakControlProps) {
  const { getValue, tweakValue, tweakDefinition, getDefinitionValue, markJukeboxDirty } = useRuntime();

  const value = definitionId
    ? getDefinitionValue(definitionId, nodeId, endpoint.id)
    : getValue(instanceId!, nodeId, endpoint.id);

  const color = getEndpointColor(endpoint.dataType, endpoint.dataFlow);

  const onChange = useCallback((val: any) => {
    if (definitionId) {
      tweakDefinition(definitionId, nodeId, endpoint.id, val);
    } else {
      tweakValue(instanceId!, nodeId, endpoint.id, val);
    }
    markJukeboxDirty();
  }, [instanceId, definitionId, nodeId, endpoint.id, tweakValue, tweakDefinition, markJukeboxDirty]);

  const { dataType, min, max, step, options, optionLabels } = endpoint;

  // Array with min/max/length: visual graph editor
  if (dataType === 'array' && min !== undefined && max !== undefined && endpoint.length) {
    return (
      <ArraySliderControl
        value={value ?? []}
        length={endpoint.length}
        min={min}
        max={max}
        step={step}
        color={color}
        trackColor="#333"
        onChange={onChange}
      />
    );
  }

  // Slider for numbers with range
  if (dataType === 'number' && min !== undefined && max !== undefined && !options?.length) {
    return (
      <TweakSlider
        value={value}
        min={min}
        max={max}
        step={step}
        color={color}
        onChange={onChange}
      />
    );
  }

  // Select
  if (options?.length) {
    return (
      <SelectControl
        value={value ?? options[0]}
        options={options as string[]}
        optionLabels={optionLabels?.length ? optionLabels : undefined}
        color={color}
        onChange={onChange}
      />
    );
  }

  // Boolean
  if (dataType === 'boolean') {
    return (
      <BooleanControl
        value={!!value}
        color={color}
        onChange={onChange}
      />
    );
  }

  // Color
  if (dataType === 'color') {
    return (
      <ColorControl
        value={value ?? { r: 0, g: 0, b: 0, a: 1 }}
        onChange={onChange}
      />
    );
  }

  // String
  if (dataType === 'string') {
    return (
      <StringControl
        value={value ?? ''}
        onChange={onChange}
      />
    );
  }

  // Fallback: display value
  return <span style={{ color: '#9ca3af', fontSize: 12 }}>{String(value ?? '')}</span>;
}
